import { Alert } from '@prisma/client';
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Button, Skeleton } from '@zond/ui';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertWithExecutions, useGetAlerts } from './alerts.queries';
import { columnOrder, columns } from './components/alerts-columns';
import { AlertsDialogs } from './components/alerts-dialogs';
import { DataTablePagination } from './components/data-table-pagination';
import DataTableToolbar from './components/data-table-toolbar';
import { AlertsProvider, useAlerts } from './context/alerts-context';

function AlertsContent() {
  const { t } = useTranslation('alerts');
  const { data: alerts, isLoading } = useGetAlerts();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const { setOpen } = useAlerts();
  const [tableData, setTableData] = useState<AlertWithExecutions[]>([]);

  useEffect(() => {
    if (alerts) {
      setTableData(alerts);
    }
  }, [alerts]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
      columnFilters: [],
      columnOrder: columnOrder,
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col h-[calc(100dvh-64px)]">
        <div className="flex-none p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold">{t('page.title')}</h1>
              <p className="text-muted-foreground">{t('page.description')}</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setOpen('create');
                }}
              >
                <Plus className="h-4 w-4" />
                {t('page.createAlert')}
              </Button>
            </div>
          </div>

          <DataTableToolbar table={table} />
        </div>

        <div className="grow overflow-auto custom-scrollbar">
          <div className="space-y-4 p-6">
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="h-10 px-2 text-left align-middle first:pl-4 last:pr-4">
                      <Skeleton className="h-4 w-24" />
                    </th>
                    <th className="h-10 px-2 text-left align-middle">
                      <Skeleton className="h-4 w-32" />
                    </th>
                    <th className="h-10 px-2 text-left align-middle">
                      <Skeleton className="h-4 w-40" />
                    </th>
                    <th className="h-10 px-2 text-left align-middle last:pr-4">
                      <Skeleton className="h-4 w-20" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array(5)
                    .fill(0)
                    .map((_, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-2 first:pl-4">
                          <Skeleton className="h-4 w-24" />
                        </td>
                        <td className="p-2">
                          <Skeleton className="h-4 w-32" />
                        </td>
                        <td className="p-2">
                          <Skeleton className="h-4 w-40" />
                        </td>
                        <td className="p-2 last:pr-4">
                          <Skeleton className="h-8 w-20" />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <Skeleton className="h-8 w-[200px]" />
              <Skeleton className="h-8 w-[120px]" />
            </div>
          </div>
        </div>
        <AlertsDialogs />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-64px)]">
      <div className="flex-none p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">{t('page.title')}</h1>
            <p className="text-muted-foreground">{t('page.description')}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setOpen('create');
              }}
            >
              <Plus className="h-4 w-4" />
              {t('page.createAlert')}
            </Button>
          </div>
        </div>

        <DataTableToolbar table={table} />
      </div>

      <div className="grow overflow-auto custom-scrollbar">
        <div className="space-y-4 p-6">
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="border-b">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:hidden first:pl-4 last:pr-4"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="border-t hover:bg-muted/50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="p-2 [&:has([role=checkbox])]:hidden first:pl-4 last:pr-4"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="h-24 text-center py-6">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <div className="text-center">
                          <h3 className="text-lg font-semibold">{t('empty.title')}</h3>
                          <p className="text-sm text-muted-foreground">{t('empty.description')}</p>
                        </div>
                        <Button
                          onClick={() => {
                            setOpen('create');
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          {t('page.createAlert')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <DataTablePagination table={table} />
        </div>
      </div>
      <AlertsDialogs />
    </div>
  );
}

export function Alerts() {
  return (
    <AlertsProvider>
      <AlertsContent />
    </AlertsProvider>
  );
}
